import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Zap, Star, Trophy } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SITES = [
  { name: "SuperBet", logo: "https://customer-assets.emergentagent.com/job_bonus-rehberi/artifacts/q6vvmvaj_Logo%20%282%29.png", link: "https://tracker.superortaklik.com/link?btag=100799042_465908" },
  { name: "VenomBet", logo: "https://customer-assets.emergentagent.com/job_bonus-rehberi/artifacts/hw7tj9k3_image.png", link: "https://tracking.ticaretbayi.com/processing/click?btag=2581_2528", scale: true },
  { name: "MaxWin", logo: "https://customer-assets.emergentagent.com/job_bonus-rehberi/artifacts/c0iovqep_Logo.png", link: "https://track.maxortaklik.com/processing/click?btag=16609_18685" },
  { name: "RamadaBet", logo: "https://customer-assets.emergentagent.com/job_bonus-rehberi/artifacts/nzy6j0ls_Logo%20%281%29.png", link: "https://ramapartner.com/processing/click?btag=16610_18686" },
  { name: "BetAsus", logo: "https://customer-assets.emergentagent.com/job_bonus-rehberi/artifacts/da9jy1n2_Header%20Logo%20%281%29.png", link: "https://asuspartner.com/links/?btag=2598563" },
  { name: "PashaGaming", logo: "https://customer-assets.emergentagent.com/job_bonus-rehberi/artifacts/n4ls75fg_Header%20Logo%20%282%29.png", link: "https://eticaretbayimiz.com/links/?btag=2598417" },
  { name: "RoyalBet", logo: "https://customer-assets.emergentagent.com/job_bonus-rehberi/artifacts/12k9bilw_Header%20Logo%20%283%29.png", link: "https://royalortaklik.com/links/?btag=2598425" },
];

const BONUSES = ["250 TL", "500 TL", "750 TL", "1000 TL", "1500 TL", "2500 TL", "5000 TL"];
const WIN_BONUSES = ["1000 TL", "1500 TL", "2500 TL", "5000 TL"];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildStrip(items, totalCount) {
  const arr = [];
  for (let i = 0; i < totalCount; i++) arr.push(items[i % items.length]);
  return arr;
}

function buildBonusStrip(totalCount, winIdx) {
  const arr = [];
  for (let i = 0; i < totalCount; i++) {
    if (i === winIdx) {
      arr.push(WIN_BONUSES[Math.floor(Math.random() * WIN_BONUSES.length)]);
    } else {
      arr.push(shuffle(BONUSES)[0]);
    }
  }
  return arr;
}

function ConfettiParticle({ delay }) {
  const colors = ['#FACC15', '#FDE047', '#F59E0B', '#FFFFFF', '#EAB308'];
  const c = colors[Math.floor(Math.random() * colors.length)];
  const left = Math.random() * 100;
  const sz = 4 + Math.random() * 8;
  const dur = 1.5 + Math.random() * 2;
  return (
    <motion.div
      className="absolute rounded-full pointer-events-none"
      style={{ left: `${left}%`, top: '-10px', width: sz, height: sz, backgroundColor: c }}
      initial={{ opacity: 1, y: 0, rotate: 0 }}
      animate={{ opacity: 0, y: 500 + Math.random() * 200, rotate: 720, x: (Math.random() - 0.5) * 300 }}
      transition={{ duration: dur, delay, ease: "easeOut" }}
    />
  );
}

function Confetti() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-50">
      {Array.from({ length: 50 }, (_, i) => <ConfettiParticle key={i} delay={Math.random() * 0.5} />)}
    </div>
  );
}

function LiveCounter() {
  const [count, setCount] = useState(2847);
  useEffect(() => {
    const iv = setInterval(() => setCount(p => p + Math.floor(Math.random() * 3) + 1), 4000);
    return () => clearInterval(iv);
  }, []);
  return (
    <div className="flex items-center justify-center gap-2 py-2.5 px-4 bg-gradient-to-r from-red-500/10 via-orange-500/10 to-red-500/10 border border-red-500/20 rounded-full" data-testid="live-counter">
      <Zap size={14} className="text-orange-400 animate-pulse" />
      <span className="text-xs md:text-sm text-zinc-300">
        Son 24 saatte <span className="text-yellow-400 font-bold">{count.toLocaleString('tr-TR')}</span> kullanıcı bonus fırsatı yakaladı
      </span>
    </div>
  );
}

/* ============ SLOT REEL ============ */
function SlotReel({ items, type, onResult, spinning }) {
  const CARD_W = 130;
  const GAP = 12;
  const STEP = CARD_W + GAP;
  const STRIP_COUNT = 50;
  const winIdxRef = useRef(35 + Math.floor(Math.random() * 7));
  const stripRef = useRef(
    type === 'bonus'
      ? buildBonusStrip(STRIP_COUNT, winIdxRef.current)
      : buildStrip(items, STRIP_COUNT)
  );
  const strip = stripRef.current;
  const finalOffset = winIdxRef.current * STEP;

  const [offset, setOffset] = useState(0);
  const raf = useRef(null);
  const done = useRef(false);

  useEffect(() => {
    if (!spinning) return;
    done.current = false;
    const start = performance.now();
    const duration = 4200;

    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 4);
      setOffset(finalOffset * ease);
      if (t < 1) {
        raf.current = requestAnimationFrame(tick);
      } else if (!done.current) {
        done.current = true;
        onResult(strip[winIdxRef.current]);
      }
    };
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [spinning]);

  return (
    <div className="relative overflow-hidden rounded-xl h-36 md:h-40" style={{ border: '2px solid rgba(250,204,21,0.3)', boxShadow: 'inset 0 0 20px rgba(250,204,21,0.05), 0 0 15px rgba(250,204,21,0.08)' }}>
      {/* Center pointer */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[2px] h-full bg-yellow-400 z-20 opacity-80" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20">
        <div className="w-0 h-0 border-l-[8px] border-r-[8px] border-t-[10px] border-l-transparent border-r-transparent border-t-yellow-400" />
      </div>
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 z-20 rotate-180">
        <div className="w-0 h-0 border-l-[8px] border-r-[8px] border-t-[10px] border-l-transparent border-r-transparent border-t-yellow-400" />
      </div>
      {/* Fade edges */}
      <div className="absolute left-0 top-0 w-16 md:w-24 h-full bg-gradient-to-r from-zinc-950 to-transparent z-10" />
      <div className="absolute right-0 top-0 w-16 md:w-24 h-full bg-gradient-to-l from-zinc-950 to-transparent z-10" />
      {/* Track */}
      <div
        className="flex items-center gap-3 h-full"
        style={{ transform: `translateX(calc(50% - 65px - ${offset}px))`, willChange: 'transform' }}
      >
        {strip.map((item, i) => (
          <div
            key={i}
            className="shrink-0 w-[130px] h-32 md:h-36 rounded-xl flex flex-col items-center justify-center transition-none"
            style={{
              background: 'linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 100%)',
              border: '2px solid rgba(250,204,21,0.5)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.6), 0 0 10px rgba(250,204,21,0.1), inset 0 1px 0 rgba(250,204,21,0.15), inset 0 -1px 0 rgba(0,0,0,0.5)',
              transform: 'perspective(800px) rotateY(0deg)',
            }}
          >
            {type === 'site' ? (
              <>
                <div className="w-24 h-14 md:w-28 md:h-16 rounded-lg bg-zinc-900 flex items-center justify-center overflow-hidden p-1.5 mb-1.5">
                  <img src={item.logo} alt={item.name} className={`w-full h-full object-contain ${item.scale ? 'scale-[2.5]' : ''}`} />
                </div>
                <span className="text-[10px] md:text-xs text-white font-medium">{item.name}</span>
              </>
            ) : (
              <>
                <Star size={18} className="text-yellow-400 mb-1" />
                <span className="text-base md:text-lg font-bold text-yellow-400" style={{ fontFamily: 'Outfit' }}>{item}</span>
                <span className="text-[9px] text-zinc-600 mt-0.5">BONUS</span>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ MAIN ============ */
export default function CaseOpening() {
  const [phase, setPhase] = useState('idle');
  const [site, setSite] = useState(null);
  const [bonus, setBonus] = useState(null);
  const [reelKey, setReelKey] = useState(0);

  const start = () => {
    setSite(null);
    setBonus(null);
    setReelKey(k => k + 1);
    setPhase('spin-site');
  };

  const onSiteResult = (s) => {
    setSite(s);
    setPhase('show-site');
    setTimeout(() => {
      setReelKey(k => k + 1);
      setPhase('spin-bonus');
    }, 1800);
  };

  const onBonusResult = (b) => {
    setBonus(b);
    setPhase('final');
    // Track case spin
    if (site) {
      axios.post(`${API}/public/track-case`, { site_name: site.name, bonus_amount: b }).catch(() => {});
    }
  };

  const reset = () => {
    setPhase('idle');
    setSite(null);
    setBonus(null);
  };

  return (
    <section className="relative py-12 md:py-20 px-4 overflow-hidden" data-testid="case-opening-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-yellow-400/5 blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto">
        <div className="flex justify-center mb-8"><LiveCounter /></div>

        <div className="text-center mb-8 md:mb-10">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Gift size={28} className="text-yellow-400" />
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight"
              style={{ fontFamily: 'Outfit', background: 'linear-gradient(135deg, #FACC15, #F59E0B, #D97706, #FACC15)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
              data-testid="case-title">
              Günlük Bonus Kasası
            </h2>
          </div>
          <p className="text-sm md:text-base text-zinc-400">Kasayı aç ve sana özel bonus fırsatını keşfet.</p>
        </div>

        <div className="relative rounded-2xl p-5 md:p-8" style={{ background: 'linear-gradient(145deg, rgba(250,204,21,0.08), rgba(0,0,0,0.6), rgba(250,204,21,0.05))', border: '3px solid rgba(250,204,21,0.5)', boxShadow: '0 0 40px rgba(250,204,21,0.15), inset 0 0 30px rgba(250,204,21,0.03), 0 20px 60px rgba(0,0,0,0.5)' }}>
          <AnimatePresence mode="wait">

            {/* === IDLE: Reel visible, not spinning + button === */}
            {phase === 'idle' && (
              <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-6">
                <SlotReel key={`idle-${reelKey}`} items={SITES} type="site" spinning={false} onResult={() => {}} />
                <div className="flex justify-center mt-8">
                  <motion.button onClick={start} className="relative group" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} data-testid="open-case-btn">
                    <div className="absolute inset-0 bg-yellow-400/30 rounded-full blur-xl group-hover:blur-2xl transition-all animate-pulse" />
                    <div className="relative bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 text-black font-bold py-4 px-12 md:px-16 rounded-full text-base md:text-lg shadow-[0_0_30px_rgba(250,204,21,0.4)] hover:shadow-[0_0_50px_rgba(250,204,21,0.6)] transition-all flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                      <Gift size={20} /> Kasayı Aç
                    </div>
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* === SPINNING SITE === */}
            {phase === 'spin-site' && (
              <motion.div key="spin-site" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-6">
                <p className="text-center text-xs text-yellow-400 font-bold uppercase tracking-[0.2em] mb-6">Site Seçiliyor...</p>
                <SlotReel key={`site-${reelKey}`} items={SITES} type="site" spinning={true} onResult={onSiteResult} />
              </motion.div>
            )}

            {/* === SITE REVEALED === */}
            {phase === 'show-site' && site && (
              <motion.div key="show-site" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} className="flex flex-col items-center py-8">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }}
                  className="w-32 h-24 md:w-40 md:h-28 rounded-2xl border-2 border-yellow-400 shadow-[0_0_40px_rgba(250,204,21,0.4)] flex items-center justify-center mb-4 bg-zinc-900 overflow-hidden p-3">
                  <img src={site.logo} alt={site.name} className={`w-full h-full object-contain ${site.scale ? 'scale-[2.5]' : ''}`} />
                </motion.div>
                <p className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{site.name}</p>
                <p className="text-xs text-yellow-400 mt-2 animate-pulse">Bonus miktarı belirleniyor...</p>
              </motion.div>
            )}

            {/* === SPINNING BONUS === */}
            {phase === 'spin-bonus' && (
              <motion.div key="spin-bonus" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-6">
                <p className="text-center text-xs text-yellow-400 font-bold uppercase tracking-[0.2em] mb-1">{site?.name} İçin Bonus Belirleniyor...</p>
                <p className="text-center text-[10px] text-zinc-600 mb-6">Bonus miktarı seçiliyor</p>
                <SlotReel key={`bonus-${reelKey}`} items={BONUSES} type="bonus" spinning={true} onResult={onBonusResult} />
              </motion.div>
            )}

            {/* === FINAL === */}
            {phase === 'final' && site && bonus && (
              <motion.div key="final" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative py-8 md:py-12">
                <Confetti />
                <motion.div className="absolute inset-0 bg-yellow-400/20 rounded-2xl pointer-events-none" initial={{ opacity: 1 }} animate={{ opacity: 0 }} transition={{ duration: 0.8 }} />
                <div className="relative z-10 flex flex-col items-center">
                  <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 150, delay: 0.2 }}>
                    <Trophy size={48} className="text-yellow-400 mb-4" />
                  </motion.div>
                  <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                    className="text-sm text-yellow-400 font-bold uppercase tracking-widest mb-2">Tebrikler!</motion.p>
                  <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                    className="text-xs text-zinc-500 mb-6">Kazandığınız Fırsat</motion.p>

                  <motion.div initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, type: "spring", stiffness: 120 }}
                    className="bg-black/60 backdrop-blur-xl border-2 border-yellow-400 rounded-2xl p-8 md:p-10 text-center shadow-[0_0_60px_rgba(250,204,21,0.3)] mb-8 min-w-[250px]">
                    <div className="w-32 h-20 md:w-40 md:h-24 rounded-xl mx-auto mb-4 flex items-center justify-center bg-zinc-900 border-2 border-yellow-400/30 overflow-hidden p-3">
                      <img src={site.logo} alt={site.name} className={`w-full h-full object-contain ${site.scale ? 'scale-[2.5]' : ''}`} />
                    </div>
                    <p className="text-xl md:text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>{site.name}</p>
                    <div className="h-px bg-gradient-to-r from-transparent via-yellow-400/40 to-transparent my-4" />
                    <p className="text-3xl md:text-4xl font-bold text-yellow-400" style={{ fontFamily: 'Outfit' }}>{bonus}</p>
                    <p className="text-xs text-zinc-500 mt-1">BONUS</p>
                  </motion.div>

                  <motion.a href={site.link || "#"} target="_blank" rel="nofollow sponsored noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
                    whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="relative group inline-block" data-testid="activate-bonus-btn">
                    <div className="absolute inset-0 bg-yellow-400/30 rounded-full blur-xl group-hover:blur-2xl transition-all" />
                    <div className="relative bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 text-black font-bold py-4 px-12 md:px-16 rounded-full text-base md:text-lg shadow-[0_0_30px_rgba(250,204,21,0.5)] hover:shadow-[0_0_50px_rgba(250,204,21,0.7)] transition-all flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                      <Zap size={20} /> BONUSU AKTİF ET
                    </div>
                  </motion.a>

                  <motion.button onClick={reset} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
                    className="mt-5 text-xs text-zinc-600 hover:text-yellow-400 transition-colors flex items-center gap-1" data-testid="try-again-btn">
                    <Gift size={12} /> Tekrar Dene
                  </motion.button>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
